import { createContext, useContext, type ReactNode } from 'react'

import type { ChatSocket } from '@/features/chat/api/useChatSocket'

/* How a thread reaches the one socket (0029).
 *
 * A context rather than a module-level singleton, because a singleton is
 * invisible to tests and to StrictMode's double-mount: the shell owns the
 * socket's lifetime, and anything that wants to watch a thread asks for it
 * through the tree it is rendered in.
 *
 * The default is INERT rather than null — subscribing does nothing when no
 * provider is above. That is what lets ChatPanel and ChatPage render in a test
 * without a socket, and it degrades the right way in production too: with no
 * provider the poll behind the socket still delivers, so a missing provider is
 * slower rather than broken.
 */
const INERT: ChatSocket = {
  connected: false,
  subscribe: () => {},
  unsubscribe: () => {},
}

const ChatSocketContext = createContext<ChatSocket>(INERT)

export function ChatSocketProvider({
  socket,
  children,
}: {
  socket: ChatSocket
  children: ReactNode
}) {
  return (
    <ChatSocketContext.Provider value={socket}>{children}</ChatSocketContext.Provider>
  )
}

/* The provider and its reader belong in one file: they are two halves of one
   context, and splitting them would leave a module whose only job is to
   re-export a `useContext` call. Fast Refresh gives up on this file as a
   result — which costs nothing, because it holds no component state to
   preserve; editing it re-mounts the tree either way. */
// eslint-disable-next-line react-refresh/only-export-components
export function useChatSocketContext(): ChatSocket {
  return useContext(ChatSocketContext)
}
